import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const CacheContext = createContext();

export const useCache = () => {
  const context = useContext(CacheContext);
  if (!context) {
    throw new Error('useCache must be used within a CacheProvider');
  }
  return context;
};

// Tiempo de expiración en milisegundos
const CACHE_EXPIRATION = {
  widgets: 5 * 60 * 1000, // 5 minutos
  domainVisits: 60 * 60 * 1000, // 1 hora
  analyzerDomains: 5 * 60 * 1000, // 5 minutos
  analyzerReports: 5 * 60 * 1000, // 5 minutos
  analyzerSubscription: 5 * 60 * 1000, // 5 minutos
  widgetsCount: 5 * 60 * 1000, // 5 minutos
  customerName: 30 * 60 * 1000, // 30 minutos
  news: 15 * 60 * 1000, // 15 minutos
};

export const CacheProvider = ({ children }) => {
  const [cache, setCache] = useState({
    widgets: {
      data: null,
      timestamp: null,
      pagination: null,
      totalItems: 0,
    },
    domainVisits: {
      data: null,
      timestamp: null,
      totalVisits: 0,
    },
    analyzerDomains: {
      data: null,
      timestamp: null,
    },
    analyzerReports: {
      data: {},
      timestamp: {},
      stats: {},
    },
    analyzerSubscription: {
      data: null,
      timestamp: null,
    },
    widgetsCount: {
      data: null,
      timestamp: null,
    },
    customerName: {
      data: null,
      timestamp: null,
    },
    news: {
      data: null,
      timestamp: null,
    },
  });

  // Usar ref para mantener referencia actual del cache sin causar re-renders
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  // Verificar si el caché está expirado - SIN dependencias para evitar recreación
  const isCacheExpired = useCallback((key, customExpiration = null) => {
    const cacheEntry = cacheRef.current[key];
    if (!cacheEntry || !cacheEntry.timestamp) {
      return true;
    }
    const now = Date.now();
    const expiration = customExpiration || CACHE_EXPIRATION[key] || 5 * 60 * 1000;
    return now - cacheEntry.timestamp > expiration;
  }, []); // ✅ Sin dependencias

  // Obtener datos del caché - SIN dependencias para evitar recreación
  const getCacheData = useCallback((key) => {
    return cacheRef.current[key];
  }, []); // ✅ Sin dependencias

  // Establecer datos en el caché
  const setCacheData = useCallback((key, data) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        ...data,
        timestamp: Date.now(),
      },
    }));
  }, []);

  // Invalidar caché específico
  const invalidateCache = useCallback((key) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        data: null,
        timestamp: null,
      },
    }));
  }, []);

  // Invalidar todo el caché
  const invalidateAllCache = useCallback(() => {
    setCache({
      widgets: { data: null, timestamp: null, pagination: null, totalItems: 0 },
      domainVisits: { data: null, timestamp: null, totalVisits: 0 },
      analyzerDomains: { data: null, timestamp: null },
      analyzerReports: { data: {}, timestamp: {}, stats: {} },
      analyzerSubscription: { data: null, timestamp: null },
      widgetsCount: { data: null, timestamp: null },
      customerName: { data: null, timestamp: null },
      news: { data: null, timestamp: null },
    });
  }, []);

  // Guardar widgets con paginación
  const setWidgetsCache = useCallback((widgets, pagination, totalItems) => {
    setCacheData('widgets', {
      data: widgets,
      pagination,
      totalItems,
    });
  }, [setCacheData]);

  // Guardar visitas de dominios
  const setDomainVisitsCache = useCallback((visitData, totalVisits) => {
    setCacheData('domainVisits', {
      data: visitData,
      totalVisits,
    });
  }, [setCacheData]);

  // Guardar dominios del analizador
  const setAnalyzerDomainsCache = useCallback((domains) => {
    setCacheData('analyzerDomains', {
      data: domains,
    });
  }, [setCacheData]);

  // Guardar reportes del analizador por dominio
  const setAnalyzerReportsCache = useCallback((domain, reports, stats) => {
    setCache(prev => ({
      ...prev,
      analyzerReports: {
        data: {
          ...prev.analyzerReports.data,
          [domain]: reports,
        },
        timestamp: {
          ...prev.analyzerReports.timestamp,
          [domain]: Date.now(),
        },
        stats: {
          ...prev.analyzerReports.stats,
          [domain]: stats,
        },
      },
    }));
  }, []);

  // Obtener reportes del analizador por dominio - SIN dependencias
  const getAnalyzerReportsCache = useCallback((domain) => {
    const reportsCache = cacheRef.current.analyzerReports;
    if (!reportsCache.data[domain] || !reportsCache.timestamp[domain]) {
      return null;
    }

    const now = Date.now();
    const expiration = CACHE_EXPIRATION.analyzerReports;
    const isExpired = now - reportsCache.timestamp[domain] > expiration;

    if (isExpired) {
      return null;
    }

    return {
      reports: reportsCache.data[domain],
      stats: reportsCache.stats[domain],
      timestamp: reportsCache.timestamp[domain],
    };
  }, []); // ✅ Sin dependencias

  // Guardar suscripción del analizador
  const setAnalyzerSubscriptionCache = useCallback((hasSubscription, service) => {
    setCacheData('analyzerSubscription', {
      data: {
        hasSubscription,
        service,
      },
    });
  }, [setCacheData]);

  // Guardar contador de widgets
  const setWidgetsCountCache = useCallback((count) => {
    setCacheData('widgetsCount', {
      data: count,
    });
  }, [setCacheData]);

  // Guardar nombre del cliente
  const setCustomerNameCache = useCallback((name) => {
    setCacheData('customerName', {
      data: name,
    });
  }, [setCacheData]);

  // Guardar noticias
  const setNewsCache = useCallback((newsData) => {
    setCacheData('news', {
      data: newsData,
    });
  }, [setCacheData]);

  const value = {
    // Funciones generales
    getCacheData,
    setCacheData,
    isCacheExpired,
    invalidateCache,
    invalidateAllCache,

    // Funciones específicas para widgets
    setWidgetsCache,
    getWidgetsCache: () => getCacheData('widgets'),

    // Funciones específicas para visitas
    setDomainVisitsCache,
    getDomainVisitsCache: () => getCacheData('domainVisits'),

    // Funciones específicas para analizador
    setAnalyzerDomainsCache,
    getAnalyzerDomainsCache: () => getCacheData('analyzerDomains'),
    setAnalyzerReportsCache,
    getAnalyzerReportsCache,
    setAnalyzerSubscriptionCache,
    getAnalyzerSubscriptionCache: () => getCacheData('analyzerSubscription'),

    // Funciones para otros datos
    setWidgetsCountCache,
    getWidgetsCountCache: () => getCacheData('widgetsCount'),
    setCustomerNameCache,
    getCustomerNameCache: () => getCacheData('customerName'),
    setNewsCache,
    getNewsCache: () => getCacheData('news'),
  };

  return (
    <CacheContext.Provider value={value}>
      {children}
    </CacheContext.Provider>
  );
};

export default CacheContext;
